import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  User,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Phone,
  Mail,
  Send,
  MessageSquare,
  Upload,
  Download,
  Users,
  Globe,
  MapPin,
  Building2,
  Menu,
  Target,
  Layers,
  RotateCcw,
  DollarSign,
  Bookmark,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import ClientAIChat from "@/components/ClientAIChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status Badge
const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Referral Submitted": "bg-blue-100 text-blue-800 border-blue-200",
    "Intake Complete": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Credit Active": "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress": "bg-purple-100 text-purple-800 border-purple-200",
    "Ready for Lender": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Loan Ready": "bg-green-100 text-green-800 border-green-200",
    default: "bg-slate-100 text-slate-800 border-slate-200",
  };
  return (
    <Badge variant="outline" className={statusStyles[status] || statusStyles.default}>
      {status || "Unknown"}
    </Badge>
  );
};

// Score Card
const ScoreCard = ({ score, label, isDarkMode }) => {
  let bgColor = isDarkMode ? "bg-zinc-800" : "bg-slate-100";
  let textColor = isDarkMode ? "text-slate-400" : "text-slate-600";
  if (score) {
    if (score >= 700) { bgColor = isDarkMode ? "bg-emerald-900/50" : "bg-emerald-100"; textColor = "text-emerald-600"; }
    else if (score >= 650) { bgColor = isDarkMode ? "bg-amber-900/50" : "bg-amber-100"; textColor = "text-amber-600"; }
    else { bgColor = isDarkMode ? "bg-red-900/50" : "bg-red-100"; textColor = "text-red-600"; }
  }
  return (
    <div className={`${bgColor} rounded-lg p-4 text-center`}>
      <p className={`text-3xl font-bold ${textColor}`}>{score || "---"}</p>
      <p className={`text-sm mt-1 ${isDarkMode ? "text-zinc-400" : "text-slate-600"}`}>{label}</p>
    </div>
  );
};

const formatGhlDate = (val) => {
  if (!val || val === "-") return "";
  if (typeof val === "number") return new Date(val).toLocaleDateString();
  if (typeof val === "string" && /^\d{10,13}$/.test(val)) return new Date(parseInt(val)).toLocaleDateString();
  if (typeof val === "string" && val.includes("T")) return new Date(val).toLocaleDateString();
  return val;
};

const ClientDashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => { fetchClientData(); }, []);

  const fetchClientData = async () => {
    try {
      const response = await authFetch(`${API}/clients/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClientData(data);
        if (data?.client_id) {
          fetchNotes(data.client_id);
          fetchDocuments(data.client_id);
          fetchAssociations(data.client_id);
          fetchPartners();
        }
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (clientId) => {
    try {
      const response = await authFetch(`${API}/clients/${clientId}/notes`, { credentials: "include" });
      if (response.ok) { const data = await response.json(); setNotes(data.notes || []); }
    } catch {}
  };

  const fetchDocuments = async (clientId) => {
    try {
      const response = await authFetch(`${API}/clients/${clientId}/documents`, { credentials: "include" });
      if (response.ok) { const data = await response.json(); setDocuments(data.documents || []); }
    } catch {}
  };

  const fetchAssociations = async (clientId) => {
    try {
      const response = await authFetch(`${API}/clients/${clientId}/associations`, { credentials: "include" });
      if (response.ok) { const data = await response.json(); setAssociations(data.associations || []); }
    } catch {}
  };

  const fetchPartners = async () => {
    try {
      const response = await authFetch(`${API}/clients/me/assigned-partners`, { credentials: "include" });
      if (response.ok) { const data = await response.json(); setPartners(data.partners || []); }
    } catch {}
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !clientData) return;
    setSubmittingNote(true);
    try {
      const response = await authFetch(`${API}/clients/${clientData.client_id}/notes`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note_text: newNote, note_type: "client" }),
      });
      if (response.ok) { toast.success("Message sent to your counselor"); setNewNote(""); fetchNotes(clientData.client_id); }
      else toast.error("Failed to send message");
    } catch { toast.error("Failed to send message"); }
    finally { setSubmittingNote(false); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !clientData) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large. Max 10MB"); return; }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await authFetch(`${API}/clients/${clientData.client_id}/documents/upload`, {
        method: "POST", credentials: "include", body: formData,
      });
      if (response.ok) { toast.success("Document uploaded"); fetchDocuments(clientData.client_id); }
      else toast.error("Failed to upload");
    } catch { toast.error("Failed to upload"); }
    finally { setUploadingDoc(false); e.target.value = ""; }
  };

  const handleDocDownload = async (doc) => {
    try {
      const response = await authFetch(`${API}/clients/${clientData.client_id}/documents/${doc.doc_id || doc.document_id}/download`, { credentials: "include" });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = doc.filename || doc.file_name; a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch { toast.error("Failed to download"); }
  };

  const c = isDarkMode ? "bg-[#265324] border-[#1a3d18]" : "";
  const cBg = isDarkMode ? "bg-[#1a3d18]" : "bg-slate-50";
  const cTxt = isDarkMode ? "text-white" : "text-slate-900";
  const cSub = isDarkMode ? "text-slate-400" : "text-slate-500";

  if (loading) {
    return (
      <div className={`flex min-h-screen ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
        <Sidebar isOpen={false} onClose={() => {}} isClient={true} />
        <main className="flex-1 md:ml-64 p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isClient={true} />

      <main className="flex-1 min-w-0 md:ml-64">
        {/* Header */}
        <header className="header-glass sticky top-0 z-30 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 hover:bg-slate-100 rounded-lg" data-testid="mobile-menu-toggle">
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold ${cTxt}`} data-testid="welcome-heading">
                  Welcome, {clientData?.first_name || user?.name?.split(" ")[0] || "Client"}
                </h1>
                <p className={`text-sm ${cSub}`}>Your credit journey at a glance</p>
              </div>
            </div>
            <StatusBadge status={clientData?.betenbough_client_status || clientData?.client_account_status || clientData?.lead_status_amp_stages} />
          </div>
        </header>

        {clientData ? (
          <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
            {/* Credit Scores */}
            <Card className={c} data-testid="scores-card">
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                  <ShieldCheck className="h-5 w-5 text-[#357c2a]" /> Your Credit Scores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <ScoreCard score={clientData.transunion_score || clientData.credit_score_transunion} label="TransUnion" isDarkMode={isDarkMode} />
                  <ScoreCard score={clientData.equifax_score || clientData.credit_score_equifax} label="Equifax" isDarkMode={isDarkMode} />
                  <ScoreCard score={clientData.experian_score || clientData.credit_score_experian} label="Experian" isDarkMode={isDarkMode} />
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList className={`grid w-full grid-cols-3 md:grid-cols-6 h-auto gap-1 ${isDarkMode ? "bg-[#1a3d18]" : ""}`}>
                <TabsTrigger value="overview" className={isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}>Overview</TabsTrigger>
                <TabsTrigger value="progress" className={isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}>Progress</TabsTrigger>
                <TabsTrigger value="report" className={`relative ${isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}`}>
                  Progress Report
                  {(clientData.opener_positives_ || clientData.construction_utilization_) && (
                    <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="gameplan" className={isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}>Game Plan</TabsTrigger>
                <TabsTrigger value="notes" className={isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}>Notes</TabsTrigger>
                <TabsTrigger value="docs" className={isDarkMode ? "data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" : ""}>Docs</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" data-testid="tab-overview">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Counselor */}
                  <Card className={c} data-testid="counselor-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <User className="h-5 w-5 text-[#357c2a]" /> Your Credit Counselor
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(clientData.betenbough_assigned_credit_counselor || clientData.assigned_counselor) ? (
                        <div className="space-y-2">
                          <p className={`font-semibold text-lg ${cTxt}`}>{clientData.betenbough_assigned_credit_counselor || clientData.assigned_counselor}</p>
                          <p className={`text-sm ${cSub}`}><Mail className="h-3 w-3 inline mr-1" />support@creditfirminc.com</p>
                          <p className={`text-sm ${cSub}`}><Phone className="h-3 w-3 inline mr-1" />(432) 599-4880</p>
                        </div>
                      ) : <p className={cSub}>A counselor will be assigned shortly</p>}
                    </CardContent>
                  </Card>

                  {/* Target Date */}
                  <Card className={c} data-testid="deadline-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <Clock className="h-5 w-5 text-amber-500" /> Target Date
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {clientData.betenbough_deadline_date_for_loan_ready ? (
                        <div>
                          <p className={`text-2xl font-bold ${cTxt}`}>{formatGhlDate(clientData.betenbough_deadline_date_for_loan_ready)}</p>
                          <p className={`text-sm mt-1 ${cSub}`}>Target loan-ready date</p>
                        </div>
                      ) : <p className={cSub}>No target date set yet</p>}
                    </CardContent>
                  </Card>

                  {/* Your Team — Assigned Partners */}
                  <Card className={`md:col-span-2 ${c}`} data-testid="account-details-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <Building2 className="h-5 w-5 text-[#357c2a]" /> Your Team
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {partners.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-4">
                          {partners.map((p, idx) => (
                            <div key={idx} className={`rounded-xl border p-4 ${isDarkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-200 bg-white"}`} data-testid={`partner-card-${idx}`}>
                              <p className={`text-xs font-medium mb-3 uppercase tracking-wide ${isDarkMode ? "text-emerald-400" : "text-[#357c2a]"}`}>{p.label}</p>
                              <div className="flex items-start gap-4">
                                {/* Profile Picture */}
                                <div className="flex-shrink-0">
                                  {p.profile_picture ? (
                                    <img src={p.profile_picture} alt={p.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#84c77f]" />
                                  ) : (
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? "bg-zinc-800" : "bg-slate-100"}`}>
                                      <User className={`h-7 w-7 ${cSub}`} />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold text-base ${cTxt}`}>{p.name}</p>
                                  {p.company_name && <p className={`text-sm ${cSub}`}>{p.company_name}</p>}
                                  {p.email && <p className={`text-sm flex items-center gap-1.5 mt-1 ${cSub}`}><Mail className="h-3 w-3 flex-shrink-0" />{p.email}</p>}
                                  {p.phone && <p className={`text-sm flex items-center gap-1.5 ${cSub}`}><Phone className="h-3 w-3 flex-shrink-0" />{p.phone}</p>}
                                  {p.website && <p className={`text-sm flex items-center gap-1.5 ${cSub}`}><Globe className="h-3 w-3 flex-shrink-0" /><a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="underline truncate">{p.website}</a></p>}
                                  {p.address && <p className={`text-sm flex items-center gap-1.5 ${cSub}`}><MapPin className="h-3 w-3 flex-shrink-0" />{p.address}</p>}
                                </div>
                                {/* Company Logo */}
                                {p.company_logo && (
                                  <div className="flex-shrink-0">
                                    <img src={p.company_logo} alt={`${p.company_name || p.name} logo`} className="h-12 w-auto max-w-[80px] object-contain" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={cSub}>No partner information available yet.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Associated Contacts */}
                  {associations.length > 0 && (
                    <Card className={`md:col-span-2 ${c}`} data-testid="associations-card">
                      <CardHeader>
                        <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                          <Users className="h-5 w-5 text-[#357c2a]" /> Associated Contacts
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-3">
                          {associations.map((assoc, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${cBg}`}>
                              <p className={`font-medium ${cTxt}`}>{assoc.name || "Contact"}</p>
                              <p className={`text-sm ${cSub}`}>{assoc.relationship || "Associated"}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Progress Tab */}
              <TabsContent value="progress" data-testid="tab-progress">
                <div className="space-y-6">
                  {/* Loan Ready Status */}
                  <Card className={c} data-testid="loan-ready-status-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <Target className="h-5 w-5 text-[#357c2a]" /> Loan Ready Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          ["Loan Ready Stage", clientData.loan_ready_stage || "-"],
                          ["Graduation Stage", clientData.client_progress || "-"],
                          ["Rescore Status", clientData.loan_ready_secondary_status || clientData.rescore_status || "-"],
                          ["Follow Up Date", formatGhlDate(clientData.loan_ready_follow_up_date) || "-"],
                          ["AU Status", clientData.au_status || clientData.au_status_pick_one || "-"],
                          ["Selected Actions", clientData.loan_ready_actions || clientData.selected_loan_ready_actions || "-"],
                        ].map(([label, val]) => (
                          <div key={label} className={`p-4 rounded-lg ${cBg}`}>
                            <p className={`text-xs mb-1 ${cSub}`}>{label}</p>
                            <p className={`font-semibold ${cTxt}`}>{val}</p>
                          </div>
                        ))}
                        <div className={`md:col-span-2 p-4 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>Case Manager Notes</p>
                          <p className={`font-medium whitespace-pre-wrap ${cTxt}`}>{clientData.case_manager_notes || "-"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Restoration Process */}
                  <Card className={c} data-testid="restoration-process-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <RotateCcw className="h-5 w-5 text-[#357c2a]" /> Restoration Process
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className={`p-4 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>Current Round</p>
                          <p className={`font-semibold ${cTxt}`}>{clientData.rounds || "-"}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>Round Date</p>
                          <p className={`font-semibold ${cTxt}`}>{formatGhlDate(clientData.round_date || clientData.round_date_due_for_update_in_50days_from_this_date) || "-"}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>Days Till Update</p>
                          <p className={`font-semibold ${cTxt}`}>{
                            clientData.days_till_due || (() => {
                              const rd = clientData.round_date || clientData.round_date_due_for_update_in_50days_from_this_date;
                              if (!rd) return "-";
                              const roundDate = typeof rd === "number" ? new Date(rd) : new Date(rd);
                              const dueDate = new Date(roundDate.getTime() + 50 * 24 * 60 * 60 * 1000);
                              const daysLeft = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                              return daysLeft > 0 ? `${daysLeft} days` : daysLeft === 0 ? "Due today" : "Past due";
                            })()
                          }</p>
                        </div>
                        <div className={`p-4 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>PR Status</p>
                          <p className={`font-semibold ${cTxt}`}>{clientData.pr_status || "-"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CSRA */}
                  <Card className={c} data-testid="csra-card">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                        <DollarSign className="h-5 w-5 text-[#357c2a]" /> Client Settlement Reserve Account (CSRA)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          ["Total Estimated Settlement", clientData.total_estimated_settlement || "-"],
                          ["CSRA Current Balance", clientData.csra_current_balance || "-"],
                          ["CSRA Payment Status", clientData.csra_payment_status || "-"],
                        ].map(([label, val]) => (
                          <div key={label} className={`p-3 rounded-lg ${cBg}`}>
                            <p className={`text-xs mb-1 ${cSub}`}>{label}</p>
                            <p className={`font-semibold ${cTxt}`}>{val}</p>
                          </div>
                        ))}
                        <div className={`p-3 rounded-lg ${cBg}`}>
                          <p className={`text-xs mb-1 ${cSub}`}>CSRA Payment Terms</p>
                          <p className={`font-medium whitespace-pre-wrap ${cTxt}`}>{clientData.csra_payment_terms_and_instructions || "-"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Progress Report Tab — mirrors affiliate/admin view */}
              <TabsContent value="report" data-testid="tab-report">
                <Card className={c} data-testid="progress-report-card">
                  <CardHeader className="pb-4">
                    <CardTitle className={`text-xl flex items-center gap-2 ${cTxt}`}>
                      <FileText className={`h-5 w-5 ${isDarkMode ? "text-[#c3ca6d]" : "text-[#357c2a]"}`} />
                      CFI Services Progress Report
                      {(clientData.opener_positives_ || clientData.construction_utilization_ || clientData.constructive_payment_history_if_applicable_and_can_click_multiple || clientData.closer_positive_statements_ || clientData.special_note_by_case_manager) ? (
                        <Badge className="bg-green-500/15 text-green-700 border-green-300 text-xs ml-2">Report Available</Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-400 border-red-300 text-xs ml-2">Pending</Badge>
                      )}
                    </CardTitle>
                    <p className={`font-semibold ${cTxt}`}>{clientData.first_name} {clientData.last_name}</p>
                    <p className={`text-sm ${cSub}`}>as of {formatGhlDate(clientData.round_date || clientData.round_date_due_for_update_in_50days_from_this_date) || "N/A"}</p>
                    {clientData.rx_or_case_manager_name_ && (
                      <p className={`text-sm ${cSub}`}>Case Manager: <span className={`font-medium ${cTxt}`}>{clientData.rx_or_case_manager_name_}</span></p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-8">
                    {/* Greeting */}
                    <div className={isDarkMode ? "text-slate-300" : "text-slate-700"}>
                      <p>Hello <strong>{clientData.first_name} {clientData.last_name}</strong>,</p>
                      <p className="mt-3">As discussed in your initial consultation, it will take anywhere from <strong>45-60 days</strong> for a round of letters to be responded to by the creditor.</p>
                      <p className="mt-3">Below is a progress report of the most recent round sent. You should see all three major credit bureaus with your current scores.</p>
                      <p className="mt-3">Please also pay close attention to the <strong>Case Manager's Notes</strong>. In these notes, the counselor will advise on any adjustments or concerns regarding your score and its progress. Don't hesitate to call your counselor with any further questions you may have.</p>
                    </div>

                    {/* Warning */}
                    <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-amber-900/20 border-amber-500" : "bg-amber-50 border-amber-500"}`}>
                      <p className={`text-xs font-bold uppercase leading-relaxed ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}>
                        THESE ARE MONITORING SCORES. LENDER SCORES WILL BE DIFFERENT. BEFORE YOU HAVE A LENDER PULL OR LOOK AT YOUR CREDIT FILE, SCHEDULE A REVIEW WITH YOUR COUNSELOR. THIS IS TO AVOID PULLING INQUIRIES DURING REPAIR; THESE SCORES ARE NOT THE FICO CLASSIC MODEL.
                      </p>
                    </div>

                    {/* Credit Scores */}
                    <div>
                      <h3 className={`text-base font-bold uppercase tracking-wide mb-4 pb-2 border-b-2 ${isDarkMode ? "text-white border-[#22c55e]" : "text-slate-900 border-teal-600"}`}>Current Credit Scores</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { label: "Experian", score: clientData.experian_score || clientData.credit_score_experian },
                          { label: "Equifax", score: clientData.equifax_score || clientData.credit_score_equifax },
                          { label: "TransUnion", score: clientData.transunion_score || clientData.credit_score_transunion },
                        ].map(({ label, score }) => (
                          <div key={label} className={`p-5 rounded-lg text-center border ${isDarkMode ? "bg-black/30 border-[#22c55e]/30" : "bg-white border-slate-200 shadow-sm"}`}>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{label}</p>
                            <p className={`text-3xl font-bold ${score ? (parseInt(score) >= 700 ? "text-emerald-500" : parseInt(score) >= 650 ? "text-amber-500" : "text-red-500") : (isDarkMode ? "text-slate-500" : "text-slate-300")}`}>{score || "---"}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Case Manager Review */}
                    <div>
                      <h3 className={`text-base font-bold uppercase tracking-wide mb-3 pb-2 border-b-2 ${isDarkMode ? "text-white border-[#22c55e]" : "text-slate-900 border-teal-600"}`}>Case Manager Review</h3>
                      <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                        Please read and review highlights of your progress as well as any important notes regarding your restoration process. Reviews will specifically highlight the major parts of your scores and how you are doing in each part. Below will be broken down to these parts: <strong>General Overview, Utilization, Payment History, Next Move</strong>, as well as any specific notes.
                      </p>
                    </div>

                    {/* General Progress */}
                    <div>
                      <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${cTxt}`}>
                        <span className="inline-block w-1.5 h-5 rounded bg-emerald-500" /> General Progress
                      </h4>
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-emerald-900/15 border-emerald-500" : "bg-emerald-50 border-emerald-500"}`}>
                        <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {clientData.opener_positives_ || <span className={`italic ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No data available yet for this section.</span>}
                        </p>
                      </div>
                    </div>

                    {/* Utilization */}
                    <div>
                      <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${cTxt}`}>
                        <span className="inline-block w-1.5 h-5 rounded bg-blue-500" /> Utilization
                      </h4>
                      <p className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                        Let's talk about your utilization — this is a huge part of the score as it makes up to <strong>35% of the overall score</strong>. It simply looks at the reporting balances of your credit cards versus your available limits, determining what percentage is being utilized and not paid off. The lower that percentage, the better! See notes from your case manager below in regards to your utilization.
                      </p>
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-blue-900/15 border-blue-500" : "bg-blue-50 border-blue-500"}`}>
                        <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {clientData.construction_utilization_ || <span className={`italic ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No data available yet for this section.</span>}
                        </p>
                      </div>
                    </div>

                    {/* Payment History */}
                    <div>
                      <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${cTxt}`}>
                        <span className="inline-block w-1.5 h-5 rounded bg-purple-500" /> Payment History
                      </h4>
                      <p className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                        <strong>45% of your score</strong> — might as well call it half! This part of the score looks at account status from a month-to-month perspective, which includes late payments, collections, and charged-offs. So important to stay current and avoid new collections going forward while we focus on cleaning up the past. Below is the case manager's review of your payment history.
                      </p>
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-purple-900/15 border-purple-500" : "bg-purple-50 border-purple-500"}`}>
                        <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {clientData.constructive_payment_history_if_applicable_and_can_click_multiple || <span className={`italic ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No data available yet for this section.</span>}
                        </p>
                      </div>
                    </div>

                    {/* Next Move */}
                    <div>
                      <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${cTxt}`}>
                        <span className="inline-block w-1.5 h-5 rounded bg-amber-500" /> Next Move
                      </h4>
                      <p className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                        Like we talked about, this is a process — each round either results in a deletion/update or builds leverage that positions us to strong-arm accordingly. Below highlights where we are at in that process as we progress:
                      </p>
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-amber-900/15 border-amber-500" : "bg-amber-50 border-amber-500"}`}>
                        <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {clientData.closer_positive_statements_ || <span className={`italic ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No data available yet for this section.</span>}
                        </p>
                      </div>
                    </div>

                    {/* Detail Notes */}
                    <div>
                      <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${cTxt}`}>
                        <span className="inline-block w-1.5 h-5 rounded bg-rose-500" /> Detail Notes
                      </h4>
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-rose-900/15 border-rose-500" : "bg-rose-50 border-rose-500"}`}>
                        <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {clientData.special_note_by_case_manager || <span className={`italic ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No detail notes from case manager at this time.</span>}
                        </p>
                      </div>
                    </div>

                    {/* Access Full Report */}
                    <div>
                      <h3 className={`text-base font-bold uppercase tracking-wide mb-3 pb-2 border-b-2 ${isDarkMode ? "text-white border-[#22c55e]" : "text-slate-900 border-teal-600"}`}>Access Your Full Report</h3>
                      <div className={`p-4 rounded-lg border ${isDarkMode ? "bg-[#22c55e]/10 border-[#22c55e]/30" : "bg-teal-50 border-teal-200"}`}>
                        <p className={`font-bold ${cTxt}`}>
                          Website: <a href="https://cfilifeplan.com" target="_blank" rel="noopener noreferrer" className={`underline ${isDarkMode ? "text-[#22c55e]" : "text-teal-700"}`}>cfilifeplan.com</a>
                        </p>
                        <p className={`mt-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}><strong>Username:</strong> {clientData.user_name || "-"}</p>
                        <p className={isDarkMode ? "text-slate-300" : "text-slate-700"}><strong>Password:</strong> credit1</p>
                      </div>
                    </div>

                    {/* Closing Text */}
                    <div className={`space-y-4 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <p>Just as a reminder, please continue to send us your <strong>correspondence from the credit bureaus</strong> and any collection agencies. Also, remember to bring in any <strong>dunning notices</strong> as soon as possible. These are collection letters that start with or contain the following language:</p>
                      <blockquote className={`pl-4 border-l-2 italic ${isDarkMode ? "border-slate-600 text-slate-400" : "border-slate-300 text-slate-500"}`}>
                        "Unless you notify this office within 30 days after receiving this notice that you dispute the validity of this debt or any portion thereof, our office will assume the debt is valid..."
                      </blockquote>
                      <p>Please continue to <strong>pay everything on time</strong> and manage your credit cards accordingly. If your counselor specified anything specific in your Client Responsibility Contract, please handle it accordingly or call if your circumstances have changed.</p>
                      <p>The plan will work, and you will achieve your goals. Your goals are our goals, and we'll meet them together. Our credit restoration program is different from all the rest, so do not listen to the cynics regarding credit repair. We ask that you continue to have the same faith with us as you had when you started. To my knowledge, there is not one credit repair organization that has come even close to the success ratios that we have.</p>
                      <blockquote className={`pl-4 border-l-2 italic ${isDarkMode ? "border-slate-600 text-slate-400" : "border-slate-300 text-slate-500"}`}>
                        As discussed in your initial consultation, any remaining derogatory items on your credit report are being worked on, and a case is being built against them. Many derogatory items will come off quickly while others will take more time. Thanks again for your confidence in us, and we look forward to helping you meet your financial goals.
                      </blockquote>
                    </div>

                    {/* Signoff */}
                    <div className={`pt-4 border-t ${isDarkMode ? "border-slate-600" : "border-slate-300"}`}>
                      <p className={`font-medium ${cTxt}`}>Sincerely,</p>
                      <p className={`font-bold text-lg ${isDarkMode ? "text-[#22c55e]" : "text-teal-700"}`}>Credit Firm Inc</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Game Plan Tab */}
              <TabsContent value="gameplan" data-testid="tab-gameplan">
                <Card className={c} data-testid="game-plan-card">
                  <CardHeader>
                    <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                      <FileText className="h-5 w-5 text-[#357c2a]" /> Game Plan Recap
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div className={`p-4 rounded-lg ${cBg}`}>
                        <p className={`text-xs mb-1 ${cSub}`}>Score Projections</p>
                        <p className={`font-semibold ${cTxt}`}>{clientData.guaranteed_score_length || clientData.guaranteed_score__length || "-"}</p>
                      </div>
                      <div className={`p-4 rounded-lg ${cBg}`}>
                        <p className={`text-xs mb-1 ${cSub}`}>Goal</p>
                        <p className={`font-semibold ${cTxt}`}>{clientData.goal || "-"}</p>
                      </div>
                      <div className={`p-4 rounded-lg ${cBg}`}>
                        <p className={`text-xs mb-1 ${cSub}`}>Goal Deadline</p>
                        <p className={`font-semibold ${cTxt}`}>{formatGhlDate(clientData.goal_deadlines) || "-"}</p>
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${cBg}`}>
                      <p className={`text-xs mb-2 ${cSub}`}>Game Plan Details</p>
                      <p className={`whitespace-pre-wrap ${isDarkMode ? "text-zinc-300" : "text-slate-700"}`}>{clientData.game_plan_recap || "No game plan recap available yet."}</p>
                    </div>
                    {clientData.betenbough_special_notes_or_credit_strategy && (
                      <div className={`p-4 rounded-lg mt-4 ${isDarkMode ? "bg-amber-900/30" : "bg-amber-50"} border ${isDarkMode ? "border-amber-800" : "border-amber-200"}`}>
                        <p className={`text-xs mb-2 ${cSub}`}>Credit Strategy Notes</p>
                        <p className={`whitespace-pre-wrap ${isDarkMode ? "text-zinc-300" : "text-slate-700"}`}>{clientData.betenbough_special_notes_or_credit_strategy}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Notes Tab */}
              <TabsContent value="notes" data-testid="tab-notes">
                <Card className={c}>
                  <CardHeader>
                    <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                      <MessageSquare className="h-5 w-5 text-[#357c2a]" /> Messages
                    </CardTitle>
                    <CardDescription>Messages between you and your counselor</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Send a message to your counselor..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          rows={3}
                          className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                          data-testid="client-note-input"
                        />
                        <div className="flex justify-end">
                          <Button onClick={handleAddNote} disabled={submittingNote || !newNote.trim()} className="bg-[#357c2a] hover:bg-[#2d6a5d]" data-testid="send-note-btn">
                            <Send className="h-4 w-4 mr-2" /> Send Message
                          </Button>
                        </div>
                      </div>
                      {notes.length > 0 ? (
                        <div className="space-y-3 mt-4">
                          {notes.map((note) => (
                            <div key={note.note_id} className={`p-4 rounded-lg ${
                              note.user_name === `${clientData.first_name} ${clientData.last_name}`
                                ? isDarkMode ? "bg-emerald-900/30 ml-8" : "bg-[#84c77f]/20 ml-8"
                                : isDarkMode ? "bg-zinc-800 mr-8" : "bg-slate-100 mr-8"
                            }`}>
                              <p className={isDarkMode ? "text-zinc-200" : "text-slate-700"}>{note.note_text}</p>
                              <p className={`text-xs mt-2 ${cSub}`}>{note.user_name} - {new Date(note.created_at).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      ) : <p className={`text-center py-4 ${cSub}`}>No messages yet. Send one to your counselor!</p>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Docs Tab */}
              <TabsContent value="docs" data-testid="tab-docs">
                <Card className={c}>
                  <CardHeader>
                    <CardTitle className={`text-lg flex items-center gap-2 ${cTxt}`}>
                      <FileText className="h-5 w-5 text-[#357c2a]" /> Documents
                    </CardTitle>
                    <CardDescription>Upload and view your documents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center ${isDarkMode ? "border-zinc-700" : "border-slate-200"}`}>
                        <input type="file" id="doc-upload" className="hidden" onChange={handleDocUpload} data-testid="client-doc-upload" />
                        <Upload className={`h-8 w-8 mx-auto mb-2 ${cSub}`} />
                        <p className={`text-sm mb-2 ${cSub}`}>Upload documents for your counselor to review</p>
                        <Button variant="outline" onClick={() => document.getElementById("doc-upload").click()} disabled={uploadingDoc} className="bg-[#357c2a] text-white hover:bg-[#2d6a5d]">
                          {uploadingDoc ? "Uploading..." : "Choose File"}
                        </Button>
                        <p className={`text-xs mt-2 ${cSub}`}>Max file size: 10MB</p>
                      </div>
                      {documents.length > 0 ? (
                        <div className="space-y-3">
                          {documents.map((doc) => (
                            <div key={doc.doc_id || doc.document_id} className={`flex items-center justify-between p-3 rounded-lg ${cBg}`}>
                              <div className="flex items-center gap-3">
                                <FileText className={`h-5 w-5 ${cSub}`} />
                                <div>
                                  <p className={`font-medium text-sm ${cTxt}`}>{doc.filename || doc.file_name}</p>
                                  <p className={`text-xs ${cSub}`}>
                                    {doc.uploaded_by_name && `${doc.uploaded_by_name} - `}
                                    {new Date(doc.uploaded_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => handleDocDownload(doc)}>
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : <p className={`text-center py-4 ${cSub}`}>No documents yet</p>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="p-6">
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold">No Client Record Found</h2>
                <p className="text-slate-600 mt-2">Please contact your representative or Credit Firm Inc support.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Chat Bot - floating */}
        {clientData?.client_id && (
          <ClientAIChat clientId={clientData.client_id} firstName={clientData.first_name} />
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
